'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellReward, Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures
} from '@tanstack/react-table'

import type { RewardsTableRowData } from '@/hooks/reward/useRewardsTable'

import { sortingBigIntFn } from '@/helpers/sorting'

import HoverableAssetReward from '../HoverableAsset/HoverableAssetReward'

// Pagination runs manually (`manualPagination`), so the table keeps the pagination feature for
// page state but no paginated row model.
export const rewardsTableFeatures = tableFeatures({
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  sortedRowModel: createSortedRowModel()
})

export type RewardsTableFeatures = typeof rewardsTableFeatures

export const rewardsColumnsDefs: ColumnDefWithSkeleton<
  RewardsTableFeatures,
  RewardsTableRowData
>[] = [
  {
    accessorKey: 'rewardToken.token.symbol',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-2xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Token
          </span>
        </TooltipTrigger>
        <TooltipContent>The token you&apos;re earning rewards in.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => <HoverableAssetReward reward={row.original} maxWidth="100px" />,
    skeletonCell: (
      <div className="gap-2xs flex items-center p-[4px_6px]">
        <div style={{ height: 24, width: 26, marginRight: 4 }}>
          <Skeleton className="h-md w-5 rounded-[20px]" />
        </div>
        <Skeleton className="h-[16px] w-[35px] rounded-[20px]" />
      </div>
    )
  },
  {
    accessorKey: 'claimable',
    sortFn: (a, b) =>
      sortingBigIntFn(a.original.claimable?.amountUsd ?? 0n, b.original.claimable?.amountUsd ?? 0n),
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-2xs text-secondary-foreground">Claimable</span>
        </TooltipTrigger>
        <TooltipContent>Rewards that are currently claimable.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <CellReward
          token={row.original.rewardToken?.token}
          amount={row.original.claimable?.amount}
          amountUsd={row.original.claimable?.amountUsd}
        />
      )
    },
    skeletonCell: (
      <div className="flex flex-col">
        <Skeleton className="h-(--text-2xs--line-height) w-[30px] rounded-[20px]" />
        <Skeleton className="h-(--text-2xs--line-height) w-[36px] rounded-[20px]" />
      </div>
    )
  },
  {
    accessorKey: 'accruing',
    sortFn: (a, b) =>
      sortingBigIntFn(a.original.accruing?.amountUsd ?? 0n, b.original.accruing?.amountUsd ?? 0n),
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-2xs text-secondary-foreground">Accruing</span>
        </TooltipTrigger>
        <TooltipContent>
          Accruing rewards that will be claimable at the end of this epoch.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <CellReward
          token={row.original.rewardToken?.token}
          amount={row.original.accruing?.amount}
          amountUsd={row.original.accruing?.amountUsd}
        />
      )
    },
    skeletonCell: (
      <div className="flex flex-col">
        <Skeleton className="h-(--text-2xs--line-height) w-[30px] rounded-[20px]" />
        <Skeleton className="h-(--text-2xs--line-height) w-[36px] rounded-[20px]" />
      </div>
    )
  }
]

export const columnLabelsRewards = {
  claimable: 'Claimable amount',
  accruing: 'Accruing amount'
}
